import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, BedDouble, Bath, Ruler, Car, MapPin, Instagram } from "lucide-react";

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div initial={{ opacity: 0, y: 36 }} whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-70px" }} transition={{ duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] }}
    className={className}>
    {children}
  </motion.div>
);

const SHED_PHOTOS = [
  { src: "/images/real-commercial-interior.jpg", label: "Interior Fit-out" },
  { src: "/images/real-commercial-site.jpg", label: "On Site" },
  { src: "/images/real-concrete-pour.jpg", label: "Foundation Pour" },
  { src: "/images/real-steel-frame.jpg", label: "Steel Frame Up" },
];

const CAMBRIDGE_RENDERS = [
  { src: "/images/cambridge-exterior.png", label: "Street Elevation" },
  { src: "/images/cambridge-rear.png", label: "Rear Elevation" },
  { src: "/images/cambridge-interior.png", label: "Open-Plan Living" },
  { src: "/images/cambridge-master.png", label: "Master Bedroom" },
];

const CAMBRIDGE_SPECS = [
  { icon: BedDouble, value: "4", label: "Bedrooms" },
  { icon: Bath, value: "2", label: "Bathrooms" },
  { icon: Ruler, value: "214m²", label: "Floor Area" },
  { icon: Car, value: "Double", label: "Garage" },
  { icon: MapPin, value: "Masterton", label: "Wairarapa" },
];

function Gallery({ photos, testid }) {
  const [active, setActive] = useState(0);
  return (
    <div data-testid={`${testid}-gallery`}>
      <div className="overflow-hidden">
        <motion.img key={active} initial={{ opacity: 0, scale: 1.02 }} animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          src={photos[active].src} alt={photos[active].label}
          className="w-full aspect-[16/9] object-cover" data-testid={`${testid}-main-image`} />
      </div>
      <div className="mt-4 flex items-center justify-between">
        <span className="text-[11px] font-bold uppercase tracking-[0.25em] text-black/50" data-testid={`${testid}-image-label`}>
          {String(active + 1).padStart(2, "0")} — {photos[active].label}
        </span>
      </div>
      <div className={`grid gap-3 mt-4`} style={{ gridTemplateColumns: `repeat(${photos.length}, minmax(0, 1fr))` }}>
        {photos.map((p, i) => (
          <button key={p.src} data-testid={`${testid}-thumb-${i + 1}`} onClick={() => setActive(i)}
            className={`overflow-hidden aspect-[4/3] transition-all duration-300 ${i === active ? "ring-2 ring-gold ring-offset-2" : "opacity-50 hover:opacity-100"}`}>
            <img src={p.src} alt={p.label} loading="lazy" className="w-full h-full object-cover" />
          </button>
        ))}
      </div>
    </div>
  );
}

function FollowButton({ href, testid }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testid}
      className="inline-flex items-center gap-2.5 mt-8 px-6 py-3.5 border border-black/20 text-[11px] font-bold uppercase tracking-[0.2em] text-black hover:border-gold hover:text-gold transition-colors duration-300 w-fit">
      <Instagram size={15} /> Follow the Build <ArrowRight size={14} />
    </a>
  );
}

function ProjectHead({ num, title, tag, badge, testid }) {
  return (
    <Reveal className="flex flex-wrap items-center gap-4 mb-10">
      <span className="text-[11px] font-bold tracking-[0.3em] text-gold">{num}</span>
      <h2 className="font-display font-semibold uppercase tracking-tight text-3xl sm:text-4xl" data-testid={testid}>
        {title}
      </h2>
      <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-black/40 border border-black/15 px-3 py-1.5">{tag}</span>
      {badge && (
        <span data-testid={`${testid}-badge`}
          className="inline-block px-4 py-1.5 bg-gold text-black text-[10px] font-bold uppercase tracking-[0.25em]">
          {badge}
        </span>
      )}
    </Reveal>
  );
}

export default function OurBuilds() {
  return (
    <main data-testid="our-builds-page" className="bg-white">
      {/* HEADER */}
      <section className="bg-black text-white pt-52 pb-20 sm:pb-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="text-[11px] font-bold uppercase tracking-[0.3em] text-gold mb-6">Selected work</motion.p>
          <div className="overflow-hidden">
            <motion.h1 initial={{ y: "100%" }} animate={{ y: 0 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
              className="font-display font-semibold uppercase leading-[0.95] tracking-tight text-5xl sm:text-7xl lg:text-8xl"
              data-testid="builds-headline">
              Our Builds
            </motion.h1>
          </div>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}
            className="mt-8 max-w-xl text-base text-white/60 leading-relaxed">
              Real projects, real photos — residential and commercial work from our crew across
              the Wairarapa, plus a first look at what's coming.
            </motion.p>
        </div>
      </section>

      {/* PROJECT 01 — RURAL RESIDENTIAL */}
      <section className="py-20 sm:py-28" data-testid="project-rural">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <ProjectHead num="01" title="Rural Family Home" tag="Residential" testid="project-rural-title" />
          <div className="grid lg:grid-cols-12 gap-12 items-end">
            <Reveal className="lg:col-span-8">
              <div className="overflow-hidden">
                <motion.img initial={{ scale: 1.06 }} whileInView={{ scale: 1 }} viewport={{ once: true }}
                  transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
                  src="/images/real-rural-home.jpg" alt="Rural family home — dark cladding, tree-lined driveway, Wairarapa mountains"
                  className="w-full aspect-[16/10] object-cover" data-testid="project-rural-image" />
              </div>
            </Reveal>
            <Reveal delay={0.15} className="lg:col-span-4">
              <p className="text-base sm:text-lg text-black/60 leading-relaxed">
                A dark-clad family home set down a tree-lined driveway, framed by the Tararua
                ranges. Built for Wairarapa conditions — warm in winter, shaded in summer,
                and sharp from every angle.
              </p>
              <p className="mt-6 text-[11px] font-bold uppercase tracking-[0.25em] text-black/45">
                Wairarapa — Completed
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PROJECT 02 — COMMERCIAL SHED */}
      <section className="py-20 sm:py-28 border-t border-black/10" data-testid="project-shed">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <ProjectHead num="02" title="Commercial Shed Build" tag="Commercial" testid="project-shed-title" />
          <Reveal>
            <Gallery photos={SHED_PHOTOS} testid="shed" />
          </Reveal>
          <Reveal delay={0.1} className="mt-12 max-w-3xl">
            <p className="text-base sm:text-lg text-black/60 leading-relaxed">
              From the foundation pour with a full crew to steel frame erection, roller doors and
              a high-bay fit-out — a complete commercial build delivered on programme. Four site
              photos, one standard: the LW&Co finish.
            </p>
            <p className="mt-6 text-[11px] font-bold uppercase tracking-[0.25em] text-black/45">
              Wairarapa — Completed
            </p>
            <FollowButton href="https://www.instagram.com/explore/tags/LWCommercialBuild/" testid="follow-build-shed" />
          </Reveal>
        </div>
      </section>

      {/* RENOVATIONS — completed renovation work, in the clients' words */}
      <section className="py-20 sm:py-28 border-t border-black/10" data-testid="project-renovations">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <ProjectHead num="03" title="Renovations" tag="Renovation" testid="project-renovations-title" />
          <Reveal className="max-w-3xl mb-12">
            <p className="text-base sm:text-lg text-black/60 leading-relaxed">
              From full home transformations to kitchen and bathroom upgrades — no job is too big
              or too small. Here's what our renovation clients say about the work.
            </p>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-px bg-black/10 border border-black/10">
            {[
              { text: "LW&Co completely transformed our bathroom and the result is stunning. The workmanship was first class and Luke's team were tidy, respectful and finished on time. Highly recommend!", name: "Mel", project: "Bathroom Renovation" },
              { text: "Our 140m² kitchen renovation was handled brilliantly by LW&Co. The design, the finish, the communication — all outstanding. Our kitchen is now the heart of our home.", name: "Miles & Maree", project: "140m² Kitchen Renovation" },
              { text: "LW&Co tackled our kitchen, bathrooms, lift shaft and extension all at once and never missed a beat. Complex job, flawless result. Luke and the team delivered exactly what they promised.", name: "Joe & Pam", project: "Kitchen, Bathrooms, Lift Shaft & Extension" },
            ].map((r, i) => (
              <Reveal key={r.name} delay={i * 0.1}>
                <figure data-testid={`reno-card-${i + 1}`}
                  className="group bg-white p-8 sm:p-10 h-full flex flex-col border-t-2 border-gold hover:bg-black transition-colors duration-500">
                  <blockquote className="text-base text-black/70 group-hover:text-white/75 leading-relaxed flex-1 transition-colors duration-500">
                    “{r.text}”
                  </blockquote>
                  <figcaption className="mt-8 pt-6 border-t border-black/10 group-hover:border-white/15 transition-colors duration-500">
                    <div className="text-sm font-bold uppercase tracking-[0.15em] group-hover:text-white transition-colors duration-500">{r.name}</div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-gold mt-1.5">{r.project}</div>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-12 flex justify-center">
            <FollowButton href="https://www.instagram.com/explore/tags/LWRenovations/" testid="follow-build-renovations" />
          </Reveal>
        </div>
      </section>

      {/* PROJECT 04 — THE CAMBRIDGE (COMING SOON, renders) */}
      <section className="py-20 sm:py-28 border-t border-black/10" data-testid="project-cambridge">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <ProjectHead num="04" title="The Cambridge" tag="Residential" badge="Coming Soon" testid="cambridge-title" />
          <Reveal>
            <Gallery photos={CAMBRIDGE_RENDERS} testid="cambridge" />
          </Reveal>
          <Reveal delay={0.1} className="mt-16 grid grid-cols-2 sm:grid-cols-5 border-y border-black/10">
            {CAMBRIDGE_SPECS.map((s) => (
              <div key={s.label} data-testid={`spec-${s.label.toLowerCase().replace(/\s/g, "-")}`}
                className="py-8 px-4 text-center border-black/10 [&:not(:last-child)]:sm:border-r">
                <s.icon size={20} className="mx-auto text-gold" strokeWidth={1.75} />
                <div className="font-display font-semibold text-2xl sm:text-3xl mt-3">{s.value}</div>
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-black/40 mt-1.5">{s.label}</div>
              </div>
            ))}
          </Reveal>
          <div className="grid lg:grid-cols-12 gap-12 mt-16">
            <Reveal className="lg:col-span-7">
              <h3 className="font-display font-semibold uppercase tracking-tight text-3xl sm:text-4xl leading-tight">
                Sharp lines. Warm living. Zero compromise.
              </h3>
              <p className="mt-6 text-base sm:text-lg text-black/60 leading-relaxed">
                The Cambridge is a four-bedroom, two-bathroom family home over 214 square metres —
                a dark gabled form opening to light-filled open-plan living, with a master retreat
                at one end and seamless flow to outdoor entertaining at the other.
              </p>
              <p className="mt-5 text-sm text-black/40 italic">
                Artist impressions shown — construction photography coming as the build progresses.
              </p>
              <FollowButton href="https://www.instagram.com/explore/tags/LWCambridgeBuild/" testid="follow-build-cambridge" />
            </Reveal>
            <Reveal delay={0.15} className="lg:col-span-5">
              <div className="bg-black text-white p-8 sm:p-10">
                <h3 className="font-display font-semibold uppercase text-2xl tracking-tight">Register your interest</h3>
                <p className="mt-4 text-sm text-white/60 leading-relaxed">
                  The Cambridge is coming soon. Be first in line for plans, pricing and build slots.
                </p>
                <Link to="/contact?topic=The%20Cambridge" data-testid="cambridge-enquire-btn"
                  className="group mt-8 inline-flex items-center gap-2 px-8 py-4 bg-gold text-black font-bold text-sm uppercase tracking-[0.15em] hover:bg-white transition-colors duration-300">
                  Enquire now <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FUTURE BUILDS */}
      <section className="pb-24 sm:pb-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal>
            <h2 className="font-display font-semibold uppercase tracking-tight text-3xl sm:text-4xl mb-10">More on the board</h2>
          </Reveal>
          <div className="grid sm:grid-cols-3 gap-5">
            {["Design No. 04", "Design No. 05", "Design No. 06"].map((name, i) => (
              <Reveal key={name} delay={i * 0.1}>
                <div data-testid={`future-build-${i + 1}`}
                  className="border-2 border-dashed border-black/15 aspect-[4/3] flex flex-col items-center justify-center text-center p-8 hover:border-gold transition-colors duration-300">
                  <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold">Future Build</span>
                  <span className="font-display font-semibold uppercase text-2xl mt-3 text-black/70">{name}</span>
                  <span className="text-xs text-black/40 mt-3 max-w-[220px]">In design — register your interest to see it first.</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
